import {
  createRouter,
  createRootRoute,
  createRoute,
  createMemoryHistory,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { createElement } from "react";
import { queryClient } from "@/lib/queryClient";
import { requireAuth } from "./guards/requireAuth";
import { LoginPage } from "@/pages/LoginPage";
import { HomePage } from "@/pages/HomePage";
import { CotacaoPage } from "@/pages/CotacaoPage";
import { PedidosPage } from "@/pages/PedidosPage";
import { CargasPage } from "@/pages/CargasPage";
import { CheckinDetailPage } from "@/pages/CheckinDetailPage";
import { MaisPage } from "@/pages/MaisPage";
import { ProdutosPage } from "@/pages/mais/ProdutosPage";
import { FornecedoresPage } from "@/pages/mais/FornecedoresPage";
import { ClientesPage } from "@/pages/mais/ClientesPage";
import { LojasPage } from "@/pages/mais/LojasPage";
import { CaixasPage } from "@/pages/mais/CaixasPage";
import { NotificacoesPage } from "@/pages/mais/NotificacoesPage";
import { UsuariosPage } from "@/pages/mais/UsuariosPage";
import { RelatoriosPage } from "@/pages/mais/RelatoriosPage";
import { PerfilPage } from "@/pages/PerfilPage";
import { DeactivatedScreen } from "@/components/auth/DeactivatedScreen";
import { QuotationDetailPage } from "@/pages/QuotationDetailPage";
import { AppShell } from "@/components/layout/AppShell";
import { getSnapshot } from "@/features/auth/authStore";
import {
  ACCESS_RULES,
  canAccess,
  type AccessRule,
} from "@/features/auth/access";
import { currentUserQueryOptions } from "@/features/auth/useCurrentUser";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { SupplierListaPage } from "@/pages/supplier/SupplierListaPage";
import { SupplierListaDetailPage } from "@/pages/supplier/SupplierListaDetailPage";
import { SupplierPedidosPage } from "@/pages/supplier/SupplierPedidosPage";
import { isValidISODate } from "@/lib/dates";

type ProdutosRouteSearch = {
  reviewRequestId?: string;
  reviewMode?: "create" | "map" | "reject";
};

type PedidosRouteSearch = {
  orderId?: string;
  fromCart?: "1";
};

type CotacaoRouteSearch = {
  editOrderId?: string;
  date?: string;
  storeId?: string;
  clientId?: string;
  quotationId?: string;
  itemId?: string;
};

type ListaRouteSearch = {
  date?: string;
};

function RootLayout() {
  return createElement(Outlet);
}

function AuthLayoutComponent() {
  return createElement(AppShell, null, createElement(Outlet));
}

function NotFoundWrapper() {
  return createElement(AppShell, null, createElement(NotFoundPage));
}

function isSupplierRole() {
  return getSnapshot()?.role === "supplier";
}

function canAccessCaixasRoute() {
  const role = getSnapshot()?.role;
  return role === "supplier" || role === "super" || role === "checker";
}

async function requireCurrentUserAccess(
  queryClient: QueryClient,
  rule: AccessRule,
) {
  const currentUser = await queryClient.ensureQueryData(
    currentUserQueryOptions,
  );

  if (!canAccess(currentUser, rule)) {
    throw redirect({ to: "/perfil" });
  }
}

function PedidosRouteComponent() {
  if (isSupplierRole()) {
    return createElement(SupplierPedidosPage);
  }
  return createElement(PedidosPage);
}

function ProdutosRouteComponent() {
  return createElement(ProdutosPage);
}

const rootRoute = createRootRoute({ component: RootLayout });

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
  beforeLoad: () => {
    const state = getSnapshot();
    const isAuthed =
      state !== null && state.expiredAt > Math.floor(Date.now() / 1000);
    if (isAuthed) {
      throw redirect({ to: "/" });
    }
  },
});

const authLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: "_auth",
  beforeLoad: ({ location }) =>
    requireAuth({ location: { href: location.href } }),
  component: AuthLayoutComponent,
});

const homeRoute = createRoute({
  getParentRoute: () => authLayout,
  path: "/",
  beforeLoad: ({ context }) =>
    requireCurrentUserAccess(context.queryClient, ACCESS_RULES.dashboard),
  component: HomePage,
});

const cotacaoRoute = createRoute({
  getParentRoute: () => authLayout,
  path: "/cotacoes",
  validateSearch: (search): CotacaoRouteSearch => ({
    editOrderId:
      typeof search.editOrderId === "string" ? search.editOrderId : undefined,
    date:
      typeof search.date === "string" && isValidISODate(search.date)
        ? search.date
        : undefined,
    storeId: typeof search.storeId === "string" ? search.storeId : undefined,
    clientId: typeof search.clientId === "string" ? search.clientId : undefined,
    quotationId:
      typeof search.quotationId === "string" ? search.quotationId : undefined,
    itemId: typeof search.itemId === "string" ? search.itemId : undefined,
  }),
  beforeLoad: ({ context }) =>
    requireCurrentUserAccess(context.queryClient, ACCESS_RULES.quotation),
  component: CotacaoPage,
});

const cotacaoDetailRoute = createRoute({
  getParentRoute: () => authLayout,
  path: "/cotacoes/$quotationId",
  beforeLoad: ({ context }) =>
    requireCurrentUserAccess(context.queryClient, ACCESS_RULES.quotation),
  component: QuotationDetailPage,
});

const pedidosRoute = createRoute({
  getParentRoute: () => authLayout,
  path: "/pedidos",
  validateSearch: (search): PedidosRouteSearch => ({
    orderId: typeof search.orderId === "string" ? search.orderId : undefined,
    fromCart:
      search.fromCart === "1" || search.fromCart === 1 ? "1" : undefined,
  }),
  beforeLoad: ({ context }) =>
    requireCurrentUserAccess(context.queryClient, ACCESS_RULES.orders),
  component: PedidosRouteComponent,
});

const listaRoute = createRoute({
  getParentRoute: () => authLayout,
  path: "/lista",
  validateSearch: (search): ListaRouteSearch => ({
    date:
      typeof search.date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(search.date)
        ? search.date
        : undefined,
  }),
  beforeLoad: () => {
    if (!isSupplierRole()) {
      throw redirect({ to: "/" });
    }
  },
  component: SupplierListaPage,
});

const listaDetailRoute = createRoute({
  getParentRoute: () => authLayout,
  path: "/lista/$quotationId",
  beforeLoad: () => {
    if (!isSupplierRole()) {
      throw redirect({ to: "/" });
    }
  },
  component: SupplierListaDetailPage,
});

const relatoriosRoute = createRoute({
  getParentRoute: () => authLayout,
  path: "/relatorios",
  beforeLoad: ({ context }) =>
    requireCurrentUserAccess(context.queryClient, ACCESS_RULES.reports),
  component: RelatoriosPage,
});

const cargasRoute = createRoute({
  getParentRoute: () => authLayout,
  path: "/cargas",
  beforeLoad: ({ context }) =>
    requireCurrentUserAccess(context.queryClient, ACCESS_RULES.checkin),
  component: CargasPage,
});

const checkinDetailRoute = createRoute({
  getParentRoute: () => authLayout,
  path: "/cargas/$checkinId",
  beforeLoad: ({ context }) =>
    requireCurrentUserAccess(context.queryClient, ACCESS_RULES.checkin),
  component: CheckinDetailPage,
});

const maisRoute = createRoute({
  getParentRoute: () => authLayout,
  path: "/mais",
  component: MaisPage,
});

const produtosRoute = createRoute({
  getParentRoute: () => authLayout,
  path: "/mais/produtos",
  validateSearch: (search): ProdutosRouteSearch => ({
    reviewRequestId:
      typeof search.reviewRequestId === "string"
        ? search.reviewRequestId
        : undefined,
    reviewMode:
      search.reviewMode === "create" ||
      search.reviewMode === "map" ||
      search.reviewMode === "reject"
        ? search.reviewMode
        : undefined,
  }),
  beforeLoad: ({ context }) =>
    requireCurrentUserAccess(context.queryClient, ACCESS_RULES.catalogProducts),
  component: ProdutosRouteComponent,
});

const fornecedoresRoute = createRoute({
  getParentRoute: () => authLayout,
  path: "/mais/fornecedores",
  beforeLoad: ({ context }) =>
    requireCurrentUserAccess(
      context.queryClient,
      ACCESS_RULES.catalogSuppliers,
    ),
  component: FornecedoresPage,
});

const clientesRoute = createRoute({
  getParentRoute: () => authLayout,
  path: "/mais/clientes",
  beforeLoad: ({ context }) =>
    requireCurrentUserAccess(context.queryClient, ACCESS_RULES.catalogClients),
  component: ClientesPage,
});

const lojasRoute = createRoute({
  getParentRoute: () => authLayout,
  path: "/mais/lojas",
  beforeLoad: ({ context }) =>
    requireCurrentUserAccess(context.queryClient, ACCESS_RULES.catalogStores),
  component: LojasPage,
});

const caixasRoute = createRoute({
  getParentRoute: () => authLayout,
  path: "/mais/caixas",
  beforeLoad: () => {
    if (!canAccessCaixasRoute()) {
      throw redirect({ to: "/" });
    }
  },
  component: CaixasPage,
});

const notificacoesRoute = createRoute({
  getParentRoute: () => authLayout,
  path: "/mais/notificacoes",
  beforeLoad: ({ context }) =>
    requireCurrentUserAccess(context.queryClient, ACCESS_RULES.alerts),
  component: NotificacoesPage,
});

const usuariosRoute = createRoute({
  getParentRoute: () => authLayout,
  path: "/mais/usuarios",
  beforeLoad: ({ context }) =>
    requireCurrentUserAccess(context.queryClient, ACCESS_RULES.users),
  component: UsuariosPage,
});

const perfilRoute = createRoute({
  getParentRoute: () => authLayout,
  path: "/perfil",
  component: PerfilPage,
});

const deactivatedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/conta-desativada",
  component: DeactivatedScreen,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  deactivatedRoute,
  authLayout.addChildren([
    homeRoute,
    cotacaoRoute,
    cotacaoDetailRoute,
    pedidosRoute,
    listaRoute,
    listaDetailRoute,
    relatoriosRoute,
    cargasRoute,
    checkinDetailRoute,
    maisRoute,
    produtosRoute,
    fornecedoresRoute,
    clientesRoute,
    lojasRoute,
    caixasRoute,
    notificacoesRoute,
    usuariosRoute,
    perfilRoute,
  ]),
]);

type CreateAppRouterOptions = {
  initialEntries?: string[];
  queryClient?: QueryClient;
};

export function createAppRouter(options: CreateAppRouterOptions = {}) {
  return createRouter({
    routeTree,
    context: { queryClient: options.queryClient ?? queryClient },
    defaultNotFoundComponent: NotFoundWrapper,
    defaultViewTransition: false,
    history: options.initialEntries
      ? createMemoryHistory({ initialEntries: options.initialEntries })
      : undefined,
  });
}

export const router = createAppRouter();

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
